import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import { decimal, money } from '@/js/shared/billables/decimal';
import { initTooltips } from '@/js/modules/tooltips';

const CC_FEE_PERCENTAGE = '2.9';
const CC_FEE_FIXED_CENTS = '30';

export default class extends Controller {
  static targets = [
    'cardCheckbox',
    'achCheckbox',
    'passFeeCheckbox',
    'passFeeLabel',
    'tooltip',
    'ccFeeRow',
    'ccFee',
    'achFeeRow',
    'achFee',
  ];

  static values = { payable: String };

  #achRequestId = 0;

  connect() {
    if (this.hasPassFeeCheckboxTarget) {
      this.#refreshFees();
    }
  }

  totalsChanged(event) {
    this.payableValue = event.detail.payable;
    this.#refreshFees();
  }

  paymentMethodChanged(event) {
    if (!this.hasPassFeeCheckboxTarget) {
      return;
    }

    const enabled = this.#onlinePaymentEnabled();
    this.passFeeLabelTarget.classList.toggle('hide', !enabled);
    if (!enabled) {
      this.passFeeCheckboxTarget.checked = false;
      this.tooltipTarget.classList.add('hide');
    }

    this.#applyPaymentMethods();
    this.#refreshFees();
  }

  refreshTooltip() {
    if (!this.hasPassFeeCheckboxTarget) {
      return;
    }

    this.#applyPaymentMethods();
    this.#reinitTooltip();
  }

  passFeeChanged(event) {
    this.tooltipTarget.classList.toggle('hide', !event.target.checked);

    if (event.target.checked) {
      this.#refreshFees();
    }
  }

  #applyPaymentMethods() {
    this.#toggleRows(this.ccFeeRowTargets, this.cardCheckboxTarget.checked);

    if (this.hasAchCheckboxTarget) {
      this.#toggleRows(this.achFeeRowTargets, this.achCheckboxTarget.checked);
    }
  }

  #toggleRows(rows, visible) {
    rows.forEach((row) => row.classList.toggle('hide', !visible));
  }

  #onlinePaymentEnabled() {
    return (
      this.cardCheckboxTarget.checked ||
      (this.hasAchCheckboxTarget && this.achCheckboxTarget.checked)
    );
  }

  #refreshFees() {
    if (this.hasCcFeeTarget) {
      this.ccFeeTarget.textContent = `$${this.#ccFee()}`;
    }

    this.#reinitTooltip();
    this.#fetchAchFee();
  }

  // Mirrors the gross-up Stripe applies when the credit card fee is pushed to
  // the client: charged = (payable + fixed) / (1 - percentage).
  #ccFee() {
    const payableCents = this.#payableCents();
    if (payableCents.lte('0')) {
      return '0.00';
    }

    const percentage = decimal(CC_FEE_PERCENTAGE).div('100');
    const chargedCents = payableCents.plus(CC_FEE_FIXED_CENTS).div(decimal('1').minus(percentage));

    return chargedCents.times(percentage).plus(CC_FEE_FIXED_CENTS).div('100').round(2).toFixed(2);
  }

  #fetchAchFee() {
    if (!this.hasAchFeeTarget) {
      return;
    }

    // Every call invalidates replies still in flight, so only the latest amount's fee is shown.
    const requestId = ++this.#achRequestId;
    const payableCents = this.#payableCents();
    if (payableCents.lte('0')) {
      this.achFeeTarget.textContent = '$0.00';
      this.#reinitTooltip();
      return;
    }

    const request = new FetchRequest('get', '/billables/xhr_ach_fee', {
      responseKind: 'json',
      query: { amount: payableCents.toFixed(0) },
    });

    request
      .perform()
      .then((response) => response.json)
      .then((data) => {
        if (requestId !== this.#achRequestId) {
          return;
        }

        this.achFeeTarget.textContent = `$${decimal(data.total_ach_fee).round(2).toFixed(2)}`;
        this.#reinitTooltip();
      })
      .catch(() => {});
  }

  #payableCents() {
    return money(this.payableValue || '0').times('100');
  }

  #reinitTooltip() {
    if (this.hasTooltipTarget) {
      initTooltips(this.tooltipTarget);
    }
  }
}
